import React, { Fragment, useState, useEffect, useLayoutEffect, useRef, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { Box, CircularProgress, IconButton, Link as MuiLink, Stack, Typography } from "@mui/material";
import { login, logout, addMfaDevice, validateUserRole } from "../../action/auth";
import { getTenantInfo } from "../../action/tenant";
import Alert from "../layout/Alert";
import UseForm from "./UseForm";
import validate from "./LoginFormValidationRules";
import ReCAPTCHA from "react-google-recaptcha";
import customConfig from "../../config";
import env from "../../env";
import MfaQRcode from "./mfaQRcode";
import MfaValidate from "./mfaValidationCode";
import ForgotPassword from "./forgotPassword";
import FormInput from "../Common/FormInputs/MuiTextField";
import AppButton from "../Common/Button/Button";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";


// const CLIENT_ID = env.GET_CLIENT_ID();
const SCOPE = process.env.REACT_APP_SCOPE;
const CAPTCHA_BASE_WIDTH = 304;

const Login = ({
  login,
  isAuthenticated,
  logout,
  getTenantInfo,
  addMfaDevice,
  qrCodeUrl,
  mfaToken,
  validateUserRole,
}) => {
  const captchaRef = React.useRef(null);
  const isMountedRef = React.useRef(true);
  const [recaptchaValue, setRecaptchaValue] = useState();
  const [showError, setShowError] = useState(false);
  const { handleSubmit, InputOnFocus, handleChange, values, errors } = UseForm(
    loginForm,
    validate,
  );

  const [isLoading, setIsLoading] = useState(false);
  const [tenantData, setTenantData] = useState({});
  const [tmsAPIError, setTmsAPIError] = useState(false);
  const [showQrCode, setShowQrCode] = useState(false);
  const [mfavalidationCode, setMfaValidationCode] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const formStackRef = useRef(null);
  const captchaContainerRef = useRef(null);
  const [captchaScale, setCaptchaScale] = useState(1);
  const [captchaRenderKey, setCaptchaRenderKey] = useState(0);
  const captchaRerenderTimerRef = useRef(null);
  const captchaChallengeTimerRef = useRef(null);
  const captchaChallengeObserverRef = useRef(null);
  const captchaChallengeStartedRef = useRef(false);

  const updateCaptchaScale = useCallback(() => {
    const el = formStackRef.current;
    if (!el) {
      return;
    }

    const width = el.getBoundingClientRect().width;
    if (!width || !Number.isFinite(width)) {
      return;
    }

    const nextScale = Math.min(1, width / CAPTCHA_BASE_WIDTH);
    setCaptchaScale((prevScale) =>
      Math.abs(prevScale - nextScale) < 0.001 ? prevScale : nextScale
    );
  }, []);

  const handleOpenForgotPassword = () => {
    if (typeof document !== "undefined" && document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    setForgotOpen(true);
  };

  const rerenderCaptcha = useCallback((delay = 0) => {
    if (typeof window === "undefined") {
      return;
    }

    if (captchaRerenderTimerRef.current) {
      window.clearTimeout(captchaRerenderTimerRef.current);
      captchaRerenderTimerRef.current = null;
    }
    if (captchaChallengeTimerRef.current) {
      window.clearTimeout(captchaChallengeTimerRef.current);
      captchaChallengeTimerRef.current = null;
    }
    captchaChallengeStartedRef.current = false;

    captchaRerenderTimerRef.current = window.setTimeout(() => {
      setCaptchaRenderKey((prev) => prev + 1);
      captchaRerenderTimerRef.current = null;
    }, delay);
  }, []);

  const startChallengeTimeoutGuard = useCallback(() => {
    if (typeof window === "undefined") {
      return;
    }
    if (captchaChallengeStartedRef.current || captchaChallengeTimerRef.current) {
      return;
    }

    captchaChallengeStartedRef.current = true;
    // Challenge expires around 2 minutes; trigger slightly earlier to normalize to
    // app-level custom message before Google iframe keeps its own inline text.
    captchaChallengeTimerRef.current = window.setTimeout(() => {
      if (!recaptchaValue) {
        setShowError(true);
        captchaRef.current?.reset?.();
        rerenderCaptcha(140);
      }
      captchaChallengeTimerRef.current = null;
      captchaChallengeStartedRef.current = false;
    }, 105000);
  }, [recaptchaValue, rerenderCaptcha]);


  useEffect(() => {
    setIsLoading(true);
    getTenantInfo()
      .then((res) => {
        setIsLoading(false);
        setTenantData(res);
        localStorage.setItem("tenantInfo", JSON.stringify(res));
      })
      .catch((err) => {
        setIsLoading(false);
        setTmsAPIError(true);
      });
  }, [getTenantInfo]);

  useEffect(() => {
    if (qrCodeUrl) {
      setShowQrCode(true);
    }
    if (mfaToken) {
      setMfaValidationCode(true);
    }
  }, [qrCodeUrl, mfaToken]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (captchaRerenderTimerRef.current && typeof window !== "undefined") {
        window.clearTimeout(captchaRerenderTimerRef.current);
      }
      if (captchaChallengeTimerRef.current && typeof window !== "undefined") {
        window.clearTimeout(captchaChallengeTimerRef.current);
      }
      if (captchaChallengeObserverRef.current) {
        captchaChallengeObserverRef.current.disconnect();
      }
    };
  }, []);

  useLayoutEffect(() => {
    updateCaptchaScale();
  }, [updateCaptchaScale]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    window.addEventListener("resize", updateCaptchaScale);
    return () => window.removeEventListener("resize", updateCaptchaScale);
  }, [updateCaptchaScale]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    // Some browser/privacy setups occasionally fail initial iframe paint until interaction.
    // Retry once automatically if the anchor iframe is still missing after mount.
    const watchdog = window.setTimeout(() => {
      const anchor = captchaContainerRef.current?.querySelector(
        'iframe[src*="recaptcha/api2/anchor"]'
      );
      if (!anchor) {
        rerenderCaptcha(0);
      }
    }, 1600);

    return () => window.clearTimeout(watchdog);
  }, [captchaRenderKey, rerenderCaptcha]);

  useEffect(() => {
    if (typeof window === "undefined" || typeof MutationObserver === "undefined") {
      return undefined;
    }

    const hasChallengeFrame = () =>
      Boolean(document.querySelector('iframe[src*="recaptcha/api2/bframe"]'));

    const checkChallengeState = () => {
      if (hasChallengeFrame() && !recaptchaValue) {
        startChallengeTimeoutGuard();
      }
    };

    checkChallengeState();
    const observer = new MutationObserver(() => checkChallengeState());
    observer.observe(document.body, { childList: true, subtree: true });
    captchaChallengeObserverRef.current = observer;

    return () => {
      observer.disconnect();
      captchaChallengeObserverRef.current = null;
    };
  }, [captchaRenderKey, recaptchaValue, startChallengeTimeoutGuard]);

  async function loginForm() {
    if (!recaptchaValue) {
      setShowError(true);
      return;
    }

    try {
      const res = await login(
        values.mobile,
        values.password,
        env.GET_CLIENT_ID(),
        SCOPE,
        recaptchaValue,
      );
      if (res?.data?.accessToken) {
        await validateUserRole(res);
        return; // do not reset
      }
    } catch (err) {
    } finally {
      if (isMountedRef.current && captchaRef.current) {
        setRecaptchaValue("");
        captchaRef.current?.reset();
      }
      // resetCaptcha(); // reset only after login finishes
    }
  }
  // const resetCaptcha = () => {
  //   captchaRef.current?.reset();
  //   setRecaptchaValue("");
  // };

  const onChangeRecaptcha = (value) => {
    // Ignore empty/null during initial widget bootstrap; some browsers emit it
    // before user interaction. Only treat it as expiry after a valid token existed.
    if (value) {
      setRecaptchaValue(value);
      setShowError(false);
      if (captchaChallengeTimerRef.current && typeof window !== "undefined") {
        window.clearTimeout(captchaChallengeTimerRef.current);
        captchaChallengeTimerRef.current = null;
      }
      captchaChallengeStartedRef.current = false;
      return;
    }

    if (recaptchaValue) {
      setRecaptchaValue("");
      setShowError(true);
      rerenderCaptcha();
    }
  };

  const errorCallback = () => {
    setRecaptchaValue("");
    setShowError(true);
    captchaRef.current?.reset?.();
  };

  const onExpiredCallback = () => {
    setRecaptchaValue("");
    setShowError(true);
    captchaRef.current?.reset?.();
    // Same behavior as error callback: avoid stale iframe message.
    rerenderCaptcha(140);
  };

  if (isAuthenticated) {
    // if(mfaToken === null){
    //   alert(customConfig.mfaEnableMsg);
    // }
    
    return <Navigate to="/landing" />;
  }

  if (showQrCode && qrCodeUrl) {
    return (
      <div style={{ display: "grid", placeItems: "center", height: "100vh" }}>
        <MfaQRcode tenantData={tenantData} />
      </div>
    );
  }

  if (mfaToken && mfavalidationCode) {
    return (
      <div style={{ display: "grid", placeItems: "center", height: "100vh" }}>
        <MfaValidate tenantData={tenantData} />
      </div>
    );
  }
  if (isLoading) {
    return (
      <div className="login-wrapper">
        <div className="login-box">
          <div
            style={{
              display: "grid",
              placeItems: "center",
              height: "100%",
            }}
          >
            <CircularProgress size="5em" style={{ color: "#AFB42B" }} />
          </div>
        </div>
      </div>
    );
  }
  if (tmsAPIError) {
    return (
      <div
        style={{
          background: "#f0f0f0",
          display: "grid",
          placeItems: "center",
          width: "100%",
          textAlign: "center",
          paddingTop: "10vh",
        }}
      >
        <div
          style={{
            width: "80%",
            height: "80vh",
            display: "grid",
            placeItems: "center",
            background: "#fcfcfc",
          }}
        >
          <div style={{ paddingBottom: 0 }}>
            <p
              style={{
                fontWeight: "bold",
                color: "#B8B8B8",
              }}
            >
              <span style={{ fontSize: 100, color: "grey" }}>500 </span>
              <span style={{ fontSize: 60, color: "#bd2d19" }}>
                Server Error
              </span>
            </p>
          </div>
          <div
            style={{
              fontSize: 25,
              textAlign: "left",
            }}
          >
            <p>Oops, something went wrong.</p>
            <br></br>
            <p>
              Try to refresh this page or feel free to contact us if the problem
              persists
            </p>
          </div>
        </div>
      </div>
    );
  }
  return (
    <Fragment>
      <div className="login-wrapper">
        <div className="login-box">
          <div className="form-box">
            <div className="row mb-0">
              <Alert />
              <div className="col s12 center-align">
                {tenantData?.tenantLogoUrl && (
                  <img
                    src={tenantData.tenantLogoUrl}
                    alt="Logo"
                    className="logo-outer"
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                  />
                )}
              </div>
            </div>
            <Box component="form" onSubmit={handleSubmit} noValidate sx={{ width: "100%" }}>
              <Stack
                ref={formStackRef}
                spacing={2.25}
                sx={{ width: "100%", maxWidth: CAPTCHA_BASE_WIDTH, mx: "auto" }}
              >
                <Box sx={{ width: "100%" }}>
                  <Typography variant="body2" sx={{ mb: 0.75, fontWeight: 600 }}>
                    User Name
                  </Typography>
                  <FormInput
                    type="text"
                    name="mobile"
                    autoComplete="username"
                    value={values.mobile || ""}
                    onChange={handleChange}
                    onInput={InputOnFocus}
                    required
                    placeholder="Enter User Name"
                    error={Boolean(errors.mobile)}
                    helperText={errors.mobile || ""}
                    touched="true"
                    fullWidth
                  />
                </Box>

                <Box sx={{ width: "100%" }}>
                  <Typography variant="body2" sx={{ mb: 0.75, fontWeight: 600 }}>
                    Password
                  </Typography>
                  <FormInput
                    type={showPassword ? "text" : "password"}
                    name="password"
                    autoComplete="current-password"
                    value={values.password || ""}
                    onChange={handleChange}
                    onInput={InputOnFocus}
                    minLength="6"
                    placeholder="Enter Password"
                    required
                    error={Boolean(errors.password)}
                    helperText={errors.password || ""}
                    touched="true"
                    fullWidth
                    endAdornment={
                      <IconButton
                        onClick={() => setShowPassword((prev) => !prev)}
                        edge="end"
                        size="small"
                        aria-label="Toggle password visibility"
                      >
                        {showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                      </IconButton>
                    }
                  />
                </Box>

                <Typography variant="body2" sx={{ color: "text.secondary", fontSize: 12 }}>
                  Forgot Password? Click to{" "}
                  <MuiLink
                    component="button"
                    type="button"
                    onClick={handleOpenForgotPassword}
                    underline="always"
                    sx={{
                      color: "primary.main",
                      fontSize: 12,
                      cursor: "pointer",
                      backgroundColor: "transparent",
                      border: 0,
                      padding: 0,
                      "&:focus": { outline: "none", boxShadow: "none", backgroundColor: "transparent" },
                      "&:focus-visible": { outline: "none", boxShadow: "none", backgroundColor: "transparent" },
                    }}
                  >
                    reset
                  </MuiLink>
                  .
                </Typography>

                <Box
                  className="capcha-box"
                  ref={captchaContainerRef}
                  sx={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Box
                    sx={{
                      width: CAPTCHA_BASE_WIDTH * captchaScale,
                      maxWidth: "100%",
                      mx: "auto",
                    }}
                  >
                    <Box
                      sx={{
                        width: CAPTCHA_BASE_WIDTH,
                        transform: `scale(${captchaScale})`,
                        transformOrigin: "top left",
                      }}
                    >
                      <ReCAPTCHA
                        key={captchaRenderKey}
                        ref={captchaRef}
                        sitekey={process.env.REACT_APP_CAPTCHA_API_KEY}
                        onChange={onChangeRecaptcha}
                        onErrored={errorCallback}
                        onExpired={onExpiredCallback}
                      />
                    </Box>
                  </Box>
                  {showError && (
                    <Typography
                      color="error"
                      variant="body2"
                      sx={{ mt: 1.25, width: "100%", textAlign: "center" }}
                    >
                      {customConfig.captchaErrorMsg}
                    </Typography>
                  )}
                </Box>

                <AppButton
                  type="submit"
                  variant="primary"
                  size="large"
                  fullWidth
                  sx={{ mt: 0.5, fontSize: 17, fontWeight: 600 }}
                >
                  Sign In
                </AppButton>
              </Stack>
            </Box>
          </div>

          {/* <div className="white-text center font-13">
            Copyright © 2020 Panasonic India Pvt Ltd.
          </div> */}
        </div>
        {/* <div className="footer login font-13  font-300i">
          Designed and Developed by Panasonic India Innovation Center
        </div>{" "} */}
        <ForgotPassword
          open={forgotOpen}
          onClose={() => setForgotOpen(false)}
        />
      </div>
    </Fragment>
  );
};
Login.propTypes = {
  login: PropTypes.func.isRequired,
  isAuthenticated: PropTypes.bool,
  qrCodeUrl: PropTypes.string,
  addMfaDevice: PropTypes.func.isRequired,
  mfaToken: PropTypes.string,
  validateUserRole: PropTypes.func.isRequired,
};
const mapStateToProps = (state) => ({
  isAuthenticated: state.auth.isAuthenticated,
  qrCodeUrl: state.auth.qrCodeUrl,
  mfaToken: state.auth.mfaToken,
});

const mapDispatchToProps = (dispatch) => {
  return {
    login: login(dispatch),
    logout: logout(dispatch),
    getTenantInfo: getTenantInfo(dispatch),
    addMfaDevice: (accessToken) => dispatch(addMfaDevice(accessToken)),
    validateUserRole: (res) => dispatch(validateUserRole(res)),
  };
};
export default connect(mapStateToProps, mapDispatchToProps)(Login);
